<template>
  <v-container fluid id="daily-report" class="pt-1">
    <!-- SIGN DIALOG SECTION -->
    <v-dialog v-model="finalizeDialog" width="500">
      <v-card>
        <v-card-title class="text-h5 lighten-2">
          Finalize Report
        </v-card-title>

        <v-card-text>
          <div class="mb-4">
            <v-alert dark dense color="error" v-if="!activities.length">
              <v-icon left>error</v-icon> Missing at least one daily activity.
            </v-alert>
            <v-alert
              dark
              dense
              color="error"
              v-if="survey && survey.some((s) => !s.answer)"
            >
              <v-icon left>error</v-icon> You have not answered all survey
              questions.
            </v-alert>
            <v-alert
              dark
              dense
              color="error"
              v-if="
                !dailyReportPhotos.some((s) => s.photoType === 'General Work')
              "
            >
              <v-icon left>error</v-icon> You must include General Work photos.
            </v-alert>
            <v-alert
              dark
              dense
              color="error"
              v-if="
                !dailyReportPhotos.some((s) => s.photoType === 'Sign In Sheets')
              "
            >
              <v-icon left>error</v-icon> You must include Sign In Sheet photos.
            </v-alert>
            <v-alert
              dark
              dense
              color="error"
              v-if="
                !dailyReportPhotos.some((s) => s.photoType === 'Osha Cards')
              "
            >
              <v-icon left>error</v-icon> You must include Osha Card photos.
            </v-alert>
            <v-alert dark dense color="error" v-if="!projectPhase">
              <v-icon left>error</v-icon> You must select a project phase.
            </v-alert>
            <v-alert dark dense color="error" v-if="!signatureData">
              <v-icon left>error</v-icon> You must provide a signature.
            </v-alert>
          </div>

          <div v-if="!signatureData" class="pa-4">
            <h4 class="mb-3">
              Digital Signature
              <span class="caption red--text">(REQUIRED)</span>
            </h4>
            <p class="caption mb-3">
              I hereby attest that the information provided above is true,
              accurate, and complete to the best of my knowledge. By signing
              below, I acknowledge my responsibility for the accuracy of this
              report.
            </p>
            <div class="signature-container mb-3">
              <VueSignature
                ref="signature"
                :w="'400px'"
                :h="'200px'"
                :options="signatureOptions"
              ></VueSignature>
              <div class="signature-instructions font-weight-thin">
                Sign here
              </div>
            </div>
            <v-btn small color="secondary" @click="clearSignature" class="mr-2">
              Clear
            </v-btn>
            <v-btn small color="primary" @click="saveSignature">
              Save Signature
            </v-btn>
          </div>

          <div v-if="signatureData" class="pa-4">
            <h4 class="mb-3">Signature Confirmed ✓</h4>
            <div class="mb-2">
              <img
                :src="signatureData"
                style="max-width: 300px; border: 1px solid #ccc;"
              />
            </div>
            <div class="caption mb-2">
              Signed by: {{ user.firstName }} {{ user.lastName }}
            </div>
            <v-btn small color="secondary" @click="clearSignature">
              Update Signature
            </v-btn>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="finalizeDialog = false">cancel</v-btn>
          <v-btn
            color="primary"
            @click="finalizeReport"
            :disabled="readyToFinalize || submitting"
            :loading="submitting"
          >
            Submit Report
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- PHOTOS DIALOG -->
    <v-dialog
      v-model="photosDialog"
      fullscreen
      transition="dialog-bottom-transition"
    >
      <v-toolbar dark color="primary" v-if="photoGroups">
        <v-btn icon dark @click="photosDialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-divider vertical class="ml-2 mr-4"></v-divider>
        <v-icon @click="carousel--" class="mr-3">
          chevron_left
        </v-icon>
        {{ carousel + 1 }}/{{
          photoGroups[carouselPhotoType] &&
            photoGroups[carouselPhotoType].length
        }}
        <v-icon @click="carousel++" class="ml-3">
          chevron_right
        </v-icon>
        <v-toolbar-title class="ml-3">
          {{ carouselPhotoType }}
        </v-toolbar-title>
        <v-spacer></v-spacer>

        <v-icon @click="downloadImage" class="mr-4">
          file_download
        </v-icon>
        <v-icon
          @click="deleteImage()"
          class="mr-2"
          :disabled="complete && user.role !== 'Office'"
        >
          delete_outline
        </v-icon>
      </v-toolbar>
      <v-card>
        <v-card-text>
          <v-carousel
            v-model="carousel"
            hide-delimiter-background
            hide-delimiters
            height="auto"
            :show-arrows="false"
            class="pt-2"
          >
            <v-carousel-item
              v-for="photo in photoGroups[carouselPhotoType]"
              :key="photo.id"
            >
              <v-img
                :src="
                  photo.url.replace(
                    'https://firebasestorage.googleapis.com/',
                    'https://ik.imagekit.io/albademo/tr:w-1400/',
                  )
                "
                style="height:90vh"
                contain
                position="top center"
              ></v-img>
            </v-carousel-item>
          </v-carousel>
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- EDIT ACTIVITIES DIALOG SECTION -->
    <v-dialog
      v-model="activitiesEditDialog"
      fullscreen
      transition="dialog-bottom-transition"
    >
      <v-toolbar color="grey lighten-3">
        <v-btn @click="deleteActivity" color="red" dark outlined
          ><v-icon left>delete</v-icon> Delete</v-btn
        >
        <v-spacer></v-spacer>
        <v-btn class="ml-4" text @click="activitiesEditDialog = false"
          >Cancel</v-btn
        >
        <v-btn
          class="ml-4"
          @click="updateActivity"
          color="primary"
          :disabled="complete && user.role !== 'Office'"
          ><v-icon left>save</v-icon> Save</v-btn
        >
      </v-toolbar>
      <v-card>
        <v-card-title> </v-card-title>

        <v-card-text v-if="currentActivity">
          <v-textarea
            outlined
            label="Note Description"
            v-model="currentActivity.activityDescription"
          ></v-textarea>
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- HEADER SECTION -->
    <v-row wrap>
      <v-col cols="12" class="headline pl-0 pt-1">
        <v-btn
          color="primary"
          class="mr-2 pa-0"
          small
          outlined
          text
          to="/daily-reports"
          style="margin-top:-3px"
          ><v-icon>chevron_left</v-icon></v-btn
        >
        Daily Report
        <v-chip color="success" small class="ml-2" v-if="!loading && complete"
          >COMPLETE</v-chip
        >
        <v-btn
          v-if="!loading && !complete && !restrictedDay"
          color="primary"
          small
          class="ml-2"
          style="margin-top:-3px"
          @click="finalizeDialog = true"
          >Submit</v-btn
        >
      </v-col>
    </v-row>
    <!-- JOBSITE AND DATE  SECTION-->
    <v-row wrap>
      <v-col cols="12" sm="12" md="12" lg="8" class="pa-0 mb-2">
        <v-card>
          <v-card-title primary-title>
            {{ jobSite && jobSite.street }}
          </v-card-title>
          <v-card-text>
            <v-btn text outlined rounded small @click="goToToday" class="mr-1"
              >TODAY</v-btn
            >
            <v-btn icon @click="goToPrevDay" small
              ><v-icon>chevron_left</v-icon></v-btn
            >
            <v-dialog
              ref="dialog"
              v-model="datePickerDialog"
              :return-value.sync="date"
              persistent
              width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  text
                  small
                  rounded
                  class="date-btn"
                  v-bind="attrs"
                  v-on="on"
                  >{{ $route.params.date | moment("ddd MMM Do YYYY") }}</v-btn
                >
              </template>
              <v-date-picker v-model="date" scrollable color="primary">
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="datePickerDialog = false">
                  Cancel
                </v-btn>
                <v-btn text color="primary" @click="goToDate(date)">
                  OK
                </v-btn>
              </v-date-picker>
            </v-dialog>

            <v-btn icon @click="goToNextDay" small
              ><v-icon>chevron_right</v-icon></v-btn
            >
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <!-- PROJECT PHASE SECTION-->
    <v-row>
      <v-col cols="12" sm="12" md="12" lg="8" class="pa-0">
        <v-card v-if="!restrictedDay" class="mb-2">
          <v-card-title class="py-3">
            Project Phase
            <span class="caption red--text ml-2">(REQUIRED)</span>
          </v-card-title>
          <v-card-text class="pb-4">
            <v-row>
              <v-col cols="12" sm="8" md="6">
                <v-select
                  v-model="projectPhase"
                  :items="[
                    { text: 'Mobilization', value: 'mobilization' },
                    { text: 'In Progress', value: 'in progress' },
                    { text: 'Demobilization', value: 'demobilization' },
                  ]"
                  label="Select Project Phase"
                  outlined
                  dense
                  :disabled="complete && user.role !== 'Office'"
                  @change="saveProjectPhase"
                ></v-select>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <!-- TABS SECTION-->
    <v-row>
      <v-col cols="12" sm="12" md="12" lg="8" class="pa-0">
        <v-card v-if="!restrictedDay">
          <v-tabs
            small
            v-model="tab"
            fixed-tabs
            show-arrows
            icons-and-text
            active-class="active-tab"
            height="50"
          >
            <v-tabs-slider color="primary"></v-tabs-slider>

            <v-tab href="#tab-1">
              SURVEY
            </v-tab>

            <v-tab href="#tab-2">
              ACTIVITIES
            </v-tab>

            <v-tab href="#tab-3">
              PHOTOS
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="tab" class="tab-item-container">
            <!-- SURVEY SECTION-->
            <v-tab-item :value="'tab-1'" class="pa-4">
              <div v-if="!loading && survey">
                <div
                  class="question mb-4"
                  v-for="sq in survey"
                  :key="sq.question"
                >
                  <strong>{{ sq.question }}</strong>
                  <br /><v-btn-toggle
                    v-model="sq.answer"
                    rounded
                    class="ml-0 mt-1"
                  >
                    <v-btn value="N/A" active-class="black--text" small>
                      N/A
                    </v-btn>
                    <v-btn value="NO" active-class="red--text" small>
                      NO
                    </v-btn>
                    <v-btn value="YES" active-class="green--text" small>
                      YES
                    </v-btn>
                  </v-btn-toggle>
                  <v-text-field
                    name="name"
                    outlined
                    v-model="sq.description"
                    dense
                    class="mt-2"
                    label="Please Describe"
                    hide-details
                  >
                  </v-text-field>
                </div>
                <div class="mt-4">
                  <v-btn
                    color="primary"
                    @click="saveSurvey"
                    :disabled="complete && user.role !== 'Office'"
                    >SAVE SURVEY</v-btn
                  >
                </div>
              </div>
              <v-progress-circular
                v-else
                indeterminate
                color="primary"
              ></v-progress-circular>
            </v-tab-item>
            <!-- ACTIVITIES SECTION-->
            <v-tab-item :value="'tab-2'" class="pa-4">
              <!-- <div class="mb-4 blue--text">
                DAILY ACTIVITIES: Describe the daily activities.
              </div> -->
              <div
                v-for="(activity, index) in activities"
                :key="index"
                class="notes-block mb-2 pa-2"
              >
                <div
                  style="display:flex;flex-direction:row;justify-content:space-between"
                >
                  <h4 class="mb-0">
                    {{ activity.activityCategory }}
                  </h4>
                  <v-btn
                    class="ml-3"
                    rounded
                    x-small
                    text
                    outlined
                    @click="editActivity(activity)"
                    >EDIT</v-btn
                  >
                </div>
                <p style="white-space:pre-line" class="mb-0">
                  {{ activity.activityDescription }}
                </p>
              </div>
              <!-- <v-divider class="my-4" v-if="activities.length"></v-divider> -->
              <div>
                <!-- ADD NEW NOTE -->
                <h4 class="mb-4 mt-4">ADD NEW NOTE</h4>
                <v-select
                  :items="['General Notes', 'Site Safety Observations']"
                  v-model="activityCategory"
                  label="Category"
                  outlined
                  dense
                >
                </v-select>
                <v-textarea
                  outlined
                  label="Note Description"
                  v-model="activityDescription"
                ></v-textarea>
                <v-btn
                  color="primary"
                  @click="addActivity"
                  :disabled="submittingActivity || complete"
                  >SAVE Activity</v-btn
                >
              </div>
            </v-tab-item>
            <!-- PHOTOS SECTION-->
            <v-tab-item :value="'tab-3'" class="pa-4">
              <v-dialog v-model="uploadingImages" persistent width="400">
                <v-card color="primary" dark>
                  <v-card-text class="pa-4">
                    <h4 class="mb-1">Uploading Photos...Please wait</h4>
                    <v-progress-linear
                      indeterminate
                      color="white"
                      class="mb-0"
                    ></v-progress-linear>
                  </v-card-text>
                </v-card>
              </v-dialog>
              <div v-for="(type, ix) in photoTypes" :key="type.name">
                <h4 class="mb-2">
                  {{ type.name }}
                  <span v-if="type.required" class="caption red--text"
                    >(REQUIRED)</span
                  >
                </h4>
                <div class="d-flex mb-4" style="flex-wrap:wrap">
                  <input
                    accept=".png, .jpg, .jpeg"
                    type="file"
                    ref="file"
                    style="display: none"
                    multiple
                    @change="previewImages(type, $event)"
                  />
                  <v-btn
                    @click="$refs.file[ix].click()"
                    style="width:100px;height:100px"
                    class="mr-2 mb-2"
                    v-if="!complete"
                    ><v-icon large color="primary">add</v-icon></v-btn
                  >
                  <v-card
                    style="width:100px;height:100px;display:inline-block;"
                    class="mr-2 mb-2"
                    v-for="(photo, ix) in photoGroups[type.name]"
                    :key="ix"
                  >
                    <v-card-text class="pa-0">
                      <a
                        @click="
                          carousel = ix;
                          carouselPhotoType = type.name;
                          photosDialog = true;
                        "
                      >
                        <img
                          :src="
                            photo.url.replace(
                              'https://firebasestorage.googleapis.com/',
                              'https://ik.imagekit.io/albademo/tr:w-100/',
                            )
                          "
                          height="100"
                          width="100"
                          style="display:inline-block"
                          class="mr-2"
                        />
                      </a>
                    </v-card-text>
                  </v-card>
                </div>
                <v-divider class="mb-4"></v-divider>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
        <v-card class="mt-1" v-if="!loading && restrictedDay && !complete">
          <v-card-text>
            <div class="pa-4">
              * Cannot submit report for this day.
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
      {{ snackText }}
      <v-btn text @click="snack = false">Close</v-btn>
    </v-snackbar>
  </v-container>
</template>
<script>
import fb from "@/main";
import moment from "moment";
import { mapState } from "vuex";
import { groupBy } from "lodash";
import VueSignature from "vue-signature";
export default {
  components: {
    VueSignature,
  },
  data() {
    return {
      submittingActivity: false,
      currentActivity: null,
      activitiesEditDialog: false,
      carouselPhotoType: null,
      datePickerDialog: false,
      photoType: null,
      photoTypes: [
        { name: "General Work", required: true },
        { name: "Sign In Sheets", required: true },
        { name: "Osha Cards", required: true },
        { name: "Delivery & Dump Tickets", required: false },
        { name: "Accidents", required: false },
        {
          name: "Pre-task / Toolbox Talks / Other Site Documents",
          required: false,
        },
      ],
      carousel: 0,
      photosDialog: false,
      finalizeDialog: false,
      option: {
        penColor: "rgb(0, 0, 0)",
        backgroundColor: "rgb(255,255,255)",
      },
      files: [],
      fileData: [],
      dailyReport: null,
      jobSiteId: null,
      dailyReportRef: null,
      date: null,
      activities: [],
      dailyReportPhotos: [],
      loading: true,
      tab: "tab-1",
      valid: false,
      submitting: false,
      submitted: false,
      modal: false,
      snack: false,
      snackColor: "",
      snackText: "",
      // new notes
      activityCategory: "General Notes",
      activityDescription: "",
      // survey
      survey: [],
      uploadingImages: false,
      complete: false,
      restrictedDay: false,
      projectPhase: "",
      signature: null,
      signatureData: null,
      signatureOptions: {
        penColor: "rgb(0, 0, 0)",
        backgroundColor: "rgb(255,255,255)",
      },
    };
  },
  computed: {
    ...mapState(["user", "jobSites"]),
    photoGroups() {
      return groupBy(this.dailyReportPhotos, "photoType");
    },
    jobSite() {
      return this.jobSites.find((j) => j.id === this.$route.params.jobSiteId);
    },
    readyToFinalize() {
      let disabled = false;
      if (!this.activities.length) {
        disabled = true;
      }
      if (this.survey && this.survey.some((s) => !s.answer)) {
        disabled = true;
      }
      if (!this.dailyReportPhotos.some((p) => p.photoType === "General Work")) {
        disabled = true;
      }
      if (
        !this.dailyReportPhotos.some((p) => p.photoType === "Sign In Sheets")
      ) {
        disabled = true;
      }
      if (!this.dailyReportPhotos.some((p) => p.photoType === "Osha Cards")) {
        disabled = true;
      }
      if (!this.projectPhase) {
        disabled = true;
      }
      if (!this.signatureData) {
        disabled = true;
      }
      return disabled;
    },
  },
  watch: {
    $route() {
      this.tab = "tab-1";
      this.loading = true;
      this.init();
    },
  },
  async created() {
    this.init();
  },
  methods: {
    async init() {
      this.loading = true;
      this.jobSiteId = this.$route.params.jobSiteId;
      this.date = this.$route.params.date;
      this.dailyReportId = `${this.date}-${this.jobSiteId}`;
      this.dailyReportRef = fb.db.doc(`dailyReports/${this.dailyReportId}`);
      this.photoType = null;
      this.dailyReportPhotos = [];
      this.survey = [];
      this.complete = false;
      this.projectPhase = "";
      this.signatureData = null;
      this.getDailyReport();
    },
    async getDailyReport() {
      try {
        const dr = await this.dailyReportRef.get();
        if (dr.exists) {
          this.restrictedDay = false;
          const dailyReport = await dr.data();
          // Load survey from document or fetch default if not exists
          if (dailyReport.survey) {
            this.survey = dailyReport.survey;
          } else {
            const surveyName = this.jobSite.surveyName || "standard";
            this.survey = await this.getSurvey(surveyName);
          }
          this.complete = dailyReport.complete;
          this.projectPhase = dailyReport.projectPhase || "";
          this.signatureData =
            dailyReport.signature && dailyReport.signature.signatureData
              ? dailyReport.signature.signatureData
              : null;
          if (this.complete) {
            if (dailyReport.completedBy.uid !== this.user.uid) {
              if (this.user.role !== "Office") {
                this.restrictedDay = true;
              }
            }
          }
          this.getDailyReportPhotos();
          this.getDailyReportActivities();
        } else {
          const threeDaysAgo = moment()
            .subtract(3, "days")
            .format("YYYY-MM-DD");
          const tomorrow = moment()
            .add(1, "days")
            .format("YYYY-MM-DD");
          if (
            this.date >= tomorrow ||
            (this.date < threeDaysAgo && this.user.role !== "Office")
          ) {
            this.loading = false;
            this.restrictedDay = true;
            return;
          } else {
            this.restrictedDay = false;
          }
          const surveyName = this.jobSite.surveyName || "standard";
          const survey = await this.getSurvey(surveyName);
          this.getDailyReportPhotos();
          this.getDailyReportActivities();
          this.survey = survey;
        }
        this.loading = false;
      } catch (error) {
        console.log(error);
        this.showSnack("error", "Error loading daily report.");
        this.loading = false;
      }
    },
    previewImages({ name: photoType }, event) {
      const files = [...event.target.files];
      if (files.length) {
        files.forEach((file) => {
          const fr = new FileReader();
          fr.readAsDataURL(file);
          fr.addEventListener("load", () => {
            this.fileData.push({
              imageUrl: fr.result,
              image: file,
            });
            if (files.length == this.fileData.length) {
              this.uploadImages(photoType);
            }
          });
        });
      } else {
        this.fileData = [];
        this.files = [];
      }
    },
    async uploadImages(photoType) {
      try {
        const lastModifiedDates = [];
        this.uploadingImages = true;
        const snapshots = await Promise.all(
          this.fileData.map((file) => {
            const rando = Math.floor(Math.random() * 10000);
            const fileName = `${this.date}/${this.jobSiteId}/${rando}`;
            lastModifiedDates.push({
              id: rando,
              lastModified: file.image.lastModified || new Date().getTime(),
            });
            return fb.dailyReportsStorage
              .ref()
              .child(fileName)
              .put(file.image);
          }),
        );
        // Get an array of file metadata
        const downloadUrls = await Promise.all(
          snapshots.map(function(r) {
            return r.ref.getDownloadURL();
          }),
        );
        var batch = fb.db.batch();
        downloadUrls.forEach((url) => {
          var docRef = fb.db.collection("dailyReportPhotos").doc();
          const modDate = lastModifiedDates.filter((l) => {
            return url.indexOf(`${l.id}?alt=media`);
          });
          batch.set(docRef, {
            url: url,
            uploadedBy: this.user,
            uploadDate: fb.timestamp,
            date: this.date,
            jobSiteId: this.jobSiteId,
            photoType: photoType,
            modifiedDate: modDate.length
              ? new Date(modDate[0].lastModified)
              : new Date(),
          });
        });
        await batch.commit();
        this.fileData = [];
        this.files = [];
        this.uploadingImages = false;
        this.getDailyReportPhotos();
        this.showSnack("success", "Uploaded Successfully.");
        this.photoType = null;
      } catch (error) {
        this.uploadingImages = false;
        console.log(error);
        this.showSnack("error", "Error Uploading Photos.");
      }
    },
    async getSurvey(surveyName) {
      const surveyRes = await fb.db.doc(`surveys/${surveyName}`).get();
      return surveyRes.data().questions;
    },
    async getDailyReportPhotos() {
      this.dailyReportPhotos = [];
      const att = await fb.db
        .collection("dailyReportPhotos")
        .where("date", "==", this.date)
        .where("jobSiteId", "==", this.jobSiteId)
        .get();
      const dailyReportPhotos = [];
      att.forEach((a) => {
        dailyReportPhotos.push({
          id: a.id,
          ...a.data(),
        });
      });
      this.dailyReportPhotos = dailyReportPhotos;
    },
    async addActivity() {
      this.submittingActivity = true;
      window.setTimeout(() => {
        this.submittingActivity = false;
      }, 500);
      await fb.db.collection("dailyReportActivities").add({
        activityCategory: this.activityCategory,
        activityDescription: this.activityDescription,
        date: this.date,
        jobSiteId: this.jobSiteId,
        createDate: fb.timestamp,
        createdBy: `${this.user.firstName} ${this.user.lastName}`,
      });
      this.showSnack();
      this.activityCategory = "General Notes";
      this.activityDescription = "";
      this.getDailyReportActivities();
    },
    editActivity(activity) {
      this.activitiesEditDialog = true;
      this.currentActivity = null;
      this.currentActivity = { ...activity };
    },
    async updateActivity() {
      await fb.db
        .doc(`dailyReportActivities/${this.currentActivity.id}`)
        .update({
          activityDescription: this.currentActivity.activityDescription,
          createDate: fb.timestamp,
        });
      this.activitiesEditDialog = false;
      this.showSnack();
      this.getDailyReportActivities();
    },
    async deleteActivity() {
      await fb.db
        .doc(`dailyReportActivities/${this.currentActivity.id}`)
        .delete();
      this.activitiesEditDialog = false;
      this.showSnack();
      this.getDailyReportActivities();
    },
    async getDailyReportActivities() {
      this.activities = [];
      const att = await fb.db
        .collection("dailyReportActivities")
        .where("date", "==", this.date)
        .where("jobSiteId", "==", this.jobSiteId)
        .get();
      const activities = [];
      att.forEach((a) => {
        activities.push({
          id: a.id,
          ...a.data(),
        });
      });
      this.activities = activities;
    },
    async saveSurvey() {
      await this.dailyReportRef.set(
        {
          survey: this.survey,
        },
        { merge: true },
      );
      this.showSnack();
    },
    async saveProjectPhase() {
      if (this.projectPhase) {
        await this.dailyReportRef.set(
          {
            projectPhase: this.projectPhase,
          },
          { merge: true },
        );
        this.showSnack("success", "Project phase saved successfully!");
      }
    },
    async saveSignature() {
      if (this.$refs.signature) {
        const isEmpty = this.$refs.signature.isEmpty();
        if (!isEmpty) {
          this.signatureData = this.$refs.signature.save();
          this.showSnack("success", "Signature saved successfully!");
        } else {
          this.showSnack("error", "Please provide a signature first.");
        }
      }
    },
    clearSignature() {
      if (this.$refs.signature) {
        this.$refs.signature.clear();
      }
      this.signatureData = null;
    },
    goToNextDay() {
      const tomorrow = moment(this.date)
        .add(1, "d")
        .format("YYYY-MM-DD");
      this.$router.push(`/daily-reports/${this.jobSiteId}/${tomorrow}`);
    },
    goToToday() {
      const today = moment().format("YYYY-MM-DD");
      const todayPath = `/daily-reports/${this.jobSiteId}/${today}`;
      if (this.$route.fullPath !== todayPath) {
        this.$router.push(todayPath);
      }
    },
    goToDate(date) {
      const todayPath = `/daily-reports/${this.jobSiteId}/${date}`;
      if (this.$route.fullPath !== todayPath) {
        this.$router.push(todayPath);
      }
      this.datePickerDialog = false;
    },
    goToPrevDay() {
      const yesterday = moment(this.date)
        .subtract(1, "d")
        .format("YYYY-MM-DD");
      this.$router.push(`/daily-reports/${this.jobSiteId}/${yesterday}`);
    },
    showSnack(color = "success", msg = "Updated successfully!") {
      this.snack = true;
      this.snackColor = color;
      this.snackText = msg;
    },
    async deleteImage() {
      try {
        const docId = this.photoGroups[this.carouselPhotoType][this.carousel]
          .id;
        const url = this.photoGroups[this.carouselPhotoType][this.carousel].url;
        var imageRef = fb.dailyReportsStorage.refFromURL(url);
        await fb.db.doc(`dailyReportPhotos/${docId}`).delete();
        await imageRef.delete();
        await this.getDailyReportPhotos();
        this.showSnack("success", "Photo deleted successfully.");
        if (!this.photoGroups[this.carouselPhotoType]) {
          this.photosDialog = false;
        }
      } catch (error) {
        console.log(error.message);
        this.showSnack("error", "Error deleting image.");
      }
    },
    async downloadImage() {
      const photo = this.photoGroups[this.carouselPhotoType][this.carousel];
      const imageKitUrl = photo.url.replace(
        "https://firebasestorage.googleapis.com/",
        "https://ik.imagekit.io/albademo/",
      );
      try {
        const response = await fetch(imageKitUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${this.carouselPhotoType.replace(/\s+/g, "_")}_${this
          .carousel + 1}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log(
          "ImageKit fetch failed, opening in new tab:",
          error.message,
        );
        window.open(photo.url, "_blank");
      }
    },
    async finalizeReport() {
      this.submitting = true;
      await this.dailyReportRef.update({
        completedBy: this.user,
        completedDate: fb.timestamp,
        complete: true,
        jobSite: this.jobSite,
        date: this.date,
        projectPhase: this.projectPhase,
        signature: {
          signatureData: this.signatureData,
          signedAt: fb.timestamp,
          signedBy: {
            uid: this.user.uid,
            firstName: this.user.firstName,
            lastName: this.user.lastName,
            email: this.user.email,
          },
        },
      });
      const saveWeather = await fb.functions.httpsCallable("saveWeather");
      saveWeather({
        dailyReportId: this.dailyReportId,
        date: this.date,
        zip: this.jobSite.postalCode,
      });
      this.finalizeDialog = false;
      this.submitting = false;
      this.showSnack("success", "Daily Report Submitted.");
      this.getDailyReport();
      // if accident, send email
      const accidentQuestion = this.survey.find(
        (s) => s.question === "Any Accidents Or Incidents On Site Today?",
      );
      if (accidentQuestion && accidentQuestion.answer === "YES") {
        const dailyReportEmails = await fb.functions.httpsCallable(
          "dailyReportEmails",
        );
        dailyReportEmails({
          type: "accidentReported",
          completedBy: this.user,
          jobSite: this.jobSite,
          accidentDescription: accidentQuestion.description || "",
        });
      }
    },
  },
};
</script>
<style scoped>
.active-tab {
  background: whitesmoke;
}
.v-tab {
  padding-left: 6px;
  padding-right: 6px;
}
.tab-item-container {
  border-top: solid 1px #efefef;
  font-size: 14px;
}
.date-btn {
  text-transform: capitalize;
}
.notes-block {
  background: #f7f7f7;
  border: solid 1px #e2e2e2;
  border-radius: 5px;
}
.photos-wrapper {
  background: #f1f1f1;
  border-radius: 5px;
}
.opacity-change {
  opacity: 0.4;
}
.signature-container {
  position: relative;
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 10px;
  background: #fafafa;
  display: inline-block;
}
.signature-instructions {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #999;
  font-size: 16px;
  pointer-events: none;
  z-index: 1;
}
</style>
